import React, { useEffect, useState } from 'react';
import { GetRequest, Tooltip } from 'components/common';

// Millimetre-per-unit factors used to convert a machine's max dimensions before comparing them against
// the (millimetre) sheet sizes. This is the picker's own table; it also covers 'infr' (fractional
// inches), which the server's LayoutCombinationsFinder::MachineSheet does not -- that engine doesn't
// consume this field, so the difference is intentional and only affects this client-side filtering.
const MILLIMETRES_PER_UNIT = { mm: 1, cm: 10, in: 25.4, infr: 25.4, feet: 304.8 };

const toMillimetres = (rawValue, unit) => {
  const number = parseFloat(rawValue);
  if (isNaN(number)) { return null; }

  const factor = MILLIMETRES_PER_UNIT[unit];
  if (factor == undefined) { return null; }

  return number * factor;
};

// Sheet-dimension picker for a printing / die-cutting unit price (printing_dimensions /
// cutting_dimensions). Offers the standard B1/B2/B3 sheets, but only those that fit within the maximum
// dimensions of the machine selected elsewhere on the price form (config.source_attribute_field). The
// machine's dimensions are read from its UserUnitPriceAttribute properties, so the list re-filters
// whenever a different machine is chosen.
const LayoutDimensionSelectFormField = ({ config, value, values, setValue, register, errors, disabled }) => {
  const [sheetOptions, setSheetOptions] = useState([]);
  const [machineProperties, setMachineProperties] = useState(null);

  useEffect(() => {
    GetRequest({
      url: `/quote-builder/layout_dimensions/sheet_options?unit=${window.user_unit}`,
      useCache: true,
      callback: (data) => { setSheetOptions(data); }
    });
  }, []);

  // The machine selected on the price form (a UserUnitPriceAttribute id).
  const machineAttributeId = values[config.source_attribute_field];

  // Load the selected machine's properties (its max dimensions) whenever the selection changes.
  // Not cached: a machine's max dimensions can be edited within the same session, and a stale cache here
  // would offer sheets that no longer fit.
  useEffect(() => {
    if (!/^\d+$/.test(String(machineAttributeId))) {
      setMachineProperties(null);
      return;
    }

    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/properties/${machineAttributeId}`,
      callback: (data) => { setMachineProperties(data); }
    });
  }, [machineAttributeId]);

  const unit = machineProperties ? machineProperties[config.source_unit] : undefined;
  const machineLongMm = machineProperties ? toMillimetres(machineProperties[config.source_max_dimension_x], unit) : null;
  const machineShortMm = machineProperties ? toMillimetres(machineProperties[config.source_max_dimension_y], unit) : null;
  const hasMachineSize = machineLongMm != null && machineShortMm != null && machineLongMm > 0 && machineShortMm > 0;

  const availableOptions = hasMachineSize
    ? sheetOptions.filter((option) => machineLongMm >= option.width_mm && machineShortMm >= option.height_mm)
    : [];

  // Drop a previously-saved sheet that no longer fits the selected machine, so we never submit a sheet
  // larger than the machine (e.g. after switching to a smaller machine).
  // `availableOptions.length` is a safe dependency because the fitting sheets are always the smallest N
  // of B1/B2/B3 (a larger sheet can't fit unless every smaller one does), so equal length => same set.
  useEffect(() => {
    if (value && hasMachineSize && !availableOptions.some((option) => String(option.id) === String(value))) {
      setValue(config.name, '');
      setValue('updated_at', Date.now());
    }
  }, [value, hasMachineSize, availableOptions.length]);

  // When a machine is selected but no standard sheet fits it, hide the whole row rather than showing a
  // dead-end picker -- there is nothing for the user to choose here.
  if (hasMachineSize && availableOptions.length === 0) {
    return null;
  }

  const onChange = (e) => {
    setValue(config.name, e.target.value);
    setValue('updated_at', Date.now());
  };

  const renderControl = () => {
    if (!hasMachineSize) {
      return <small className="form-text text-muted">{t('quote_builder.select_machine_for_sheet')}</small>;
    }

    return (
      <select
        value={value || ''}
        name={config.name}
        disabled={disabled}
        className={`${errors[config.name] ? 'input-error form-control general-frm-element' : 'form-control general-frm-element'}`}
        {...register(config.name, { onChange: onChange })}
      >
        <option value="">{t('quote_builder.select_sheet_dimension')}</option>
        {availableOptions.map((option) => {
          return <option key={option.id} value={option.id}>{option.description}</option>;
        })}
      </select>
    );
  };

  return (
    <tr>
      <td>
        {t(config.label)} {config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} />}
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        {renderControl()}
        {errors[config.name] && <small className="error"> {errors[config.name].message} </small>}
      </td>
    </tr>
  );
};

export default LayoutDimensionSelectFormField;
